<template>
	<div class="generic-tabs-wrapper" :class="{	'mobile': getMobileAppState }">
		<!-- Arrow Left -->
		<transition name="appear">
			<div class="icon-wrapper"
			v-if="showLeftArrow && !getMobileAppState"
			:class="{ 'left': showLeftArrow && !getMobileAppState }"
			:style="`background-color: var(--${backgroundColor})`">
				<buttons-quaternary
				icon="arrow-left"
				class="icon"
				@clicked="moveLeftHandler"/>
			</div>
		</transition>
		<!-- Tabs-Wrapper -->
		<div
		v-dragscroll="getMobileAppState"
		ref="tabsWrapper"
		class="tabs-wrapper"
		:class="{ 'right': showRightArrow && !getMobileAppState, 'left': showLeftArrow && !getMobileAppState  }">
			<div ref="tabsContainer" class="tabs-container">
				<!-- Tabs -->
				<div
				v-for="tab in tabsList"
				class="tab"
				:key="tab.id"
				:class="[{ 'selected': tab.selected, 'disabled': tab.disabled }, tab.state]"
				@click="emit('clicked', tab.id)">
					<component v-if="tab.icon || tab.state" :is="iconComponent(tab)"/>
					{{tab.label}}
				</div>
			</div>
		</div>
		<!-- Arrow Right -->
		<transition name="appear">
			<div class="icon-wrapper"
			v-if="showRightArrow && !getMobileAppState"
			:class="{ 'right': showRightArrow && !getMobileAppState }"
			:style="`background-color: var(--${backgroundColor})`">
				<buttons-quaternary
				icon="arrow-right"
				class="icon"
				@clicked="moveRightHandler"/>
			</div>
		</transition>
	</div>
</template>

<script setup>
import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'
/* Vue */
import { ref, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent, nextTick } from 'vue'
/* Aux */
import { scrollTo } from '@/assets/scripts/scrollAnimation'

/* Store & mapGetters */
import { mapGetters } from '@/store/helpers/mappers'
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@backgroundColor {String} background color in case is not the usual one
@tabsList {Array} in which each object has:
	- id { String, Number }
	- label { String }
	- selected { Boolean }
	- icon { String } - icon to display on the tab (optional)
	- state { String } - can be [negative, positive] (default is no state)
	- disabeld { Boolean }
*/
const props = defineProps({
	tabsList: Array,
	backgroundColor: {
		type: String,
		default: 'secondary-light-3'
	}
})

const iconComponent = (tab) => {
	const selectedIcon = !tab.state ? tab.icon : tab.state === 'negative' ? 'error' : 'check-circle'
	return defineAsyncComponent(() => import('@/assets/icons/' + selectedIcon + '.svg?inline'))
}
/*************************************************
*                                                *
*           Apply Scroll Validation Logic        *
*                                                *
*************************************************/
const tabsWrapper = ref('')
const hasScroll = ref(false)

const scrollController = () => {
	hasScroll.value = tabsWrapper.value.scrollWidth > tabsWrapper.value.offsetWidth
	nextTick(() => {
		findSelectedTab()
	})
}

/*************************************************
*                                                *
*            Animate Selected Line Logic         *
*                                                *
*************************************************/
const tabsContainer = ref('')
const scrollPosition = ref(0)
const selectedTab = ref()

watch(() => props.tabsList, () => {
	findSelectedTab()
})

watch(props.tabsList, () => {
	findSelectedTab()
})

const findSelectedTab = () => {
	const selectedIndex = props.tabsList.findIndex(ele => ele.selected)

	if (tabsContainer.value.children) {
		selectedTab.value = tabsContainer.value.children[selectedIndex]
		selectHandler()
	}
}

const selectHandler = () => {
	const currentScrollPosition = tabsWrapper.value.scrollLeft

	/* For last and first tab we will force the value of scroll to be min possible and max possible */
	if (props.tabsList.length === (props.tabsList.findIndex(ele => ele.selected) + 1)) {
		scrollTo(tabsWrapper.value, tabsWrapper.value.scrollWidth, reSetScrollVariable)
		return
	} else if (props.tabsList.findIndex(ele => ele.selected) === 0) {
		scrollTo(tabsWrapper.value, 0, reSetScrollVariable)
		return
	}

	/* Validating if selected element is hidden left and fixing position if so */
	if (selectedTab.value?.offsetLeft < currentScrollPosition) {
		scrollTo(tabsWrapper.value, selectedTab.value.offsetLeft - 40, reSetScrollVariable)
	}
	/* Validating if selected element is hidden right */
	if ((selectedTab.value?.offsetLeft + selectedTab.value?.clientWidth) > (tabsContainer.value?.clientWidth + currentScrollPosition)) {
		const temp = Math.abs((tabsContainer.value.clientWidth + currentScrollPosition) - (selectedTab.value.offsetLeft + 40 + selectedTab.value.clientWidth))
		scrollTo(tabsWrapper.value, currentScrollPosition + temp, reSetScrollVariable)
	}
}
/*************************************************
*                                                *
*                  	Arrows Logic               	 *
*                                                *
*************************************************/
const showLeftArrow = computed(() => {
	return scrollPosition.value > 0
})

const showRightArrow = computed(() => {
	return Math.ceil(tabsContainer.value.offsetWidth + scrollPosition.value) < tabsContainer.value.scrollWidth
})

/*
* Move tabs container clientWidth size to the right
* This means, if our total size is 400, we will increment 400 to scrollPosition
*/
const moveRightHandler = () => {
	const temp = tabsWrapper.value.scrollLeft + tabsWrapper.value.offsetWidth
	scrollTo(tabsWrapper.value, temp, reSetScrollVariable)
}
/*
* Move tabs container clientWidth size to the left
* This means, if our total size is 400, we will decrement 400 to scrollPosition
*/
const moveLeftHandler = () => {
	const temp = tabsWrapper.value.scrollLeft - tabsWrapper.value.offsetWidth
	scrollTo(tabsWrapper.value, temp, reSetScrollVariable)
}

const reSetScrollVariable = () => {
	scrollPosition.value = tabsWrapper.value?.scrollLeft
}

onMounted(() => {
	scrollController()
	window.addEventListener('resize', scrollController)

	setTimeout(() => {
		findSelectedTab()
	}, 200)
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', scrollController)
})
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
	width: 100%;
    max-width: 100%;
	height: 40px;

	display: flex;
	position: relative;

	.tabs-wrapper {
		width: 100%;
		overflow: hidden;

		.tabs-container {
			height: 100%;

			display: flex;
			justify-content: flex-start;
			white-space: nowrap;
			position: relative;

			.tab {
				height: 100%;
				width: fit-content;

				padding: 0 16px;

				display: flex;
				align-items: center;
				flex-shrink: 0;
				flex-grow: 0;

				@include H7-Medium;
				color: var(--secondary-dark-1);
				border-bottom: 1px solid var(--secondary-dark-1);

				cursor: pointer;

				svg {
					margin-right: 8px;
				}

				&:not(:last-of-type) {
					margin-right: 2px;
				}

				&.selected {
					color: var(--primary);
					border-width: 2px;
					border-color: var(--primary);

					:deep(svg) {
						.change-color-fill {
							fill: var(--primary);
						}
					}
				}

				&.disabled {
					border-bottom: 1px solid var(--secondary-dark-1);

					color: var(--secondary-dark-1);
					opacity: 0.16;

					cursor: unset;
					pointer-events: none;
				}

				@media (hover: hover) and (pointer: fine) {
					&:hover {
						border-radius: 8px 8px 0 0;
						background-color: var(--secondary-light-2);
					}
                }

				&:active {
					border-radius: 8px 8px 0 0;
					background-color: var(--secondary-light-1);
				}

				&.positive {
					color: var(--positive);
					border-color: var(--positive);

					:deep(svg) {
						.change-color-fill {
							fill: var(--positive);
						}
					}
				}

				&.negative {
					color: var(--negative);
					border-color: var(--negative);

					:deep(svg) {
						.change-color-fill {
							fill: var(--negative);
						}
					}
				}
			}
		}
	}

	.icon-wrapper {
		position: absolute;
		z-index: 3;
		transition: all .3s cubic-bezier(.25,.8,.5,1);

		&.left {
			left: 0;
		}

		&.right {
			right: 0;
		}

		&.appear-enter-active, &.appear-leave-active{
			opacity: 1;
		}

		&.appear-enter-from, &.appear-leave-to{
			opacity: 0;
		}
	}

	&.mobile {
		.tabs-wrapper {
			padding: 0 16px;
		}
	}
}
</style>
